import React, { useEffect, useState } from "react";
import { ReportAPI } from "../../api/apiClient";

interface DRSNote {
  drsNotesNo: number;
  noteType: string;
  note: string;
  remarks: string;
  by: string;
  drs: number;
  drsDate: string;
  imageDetail: number;
  lastSession: number;
  changeType: string;
  imageBase64?: string;
}

interface GroupedNotes {
  noteType: string;
  notes: DRSNote[];
}

interface Props {
  projectId: number;
}

export default function DRSNotesReport({ projectId }: Props) {

  const [data, setData] = useState<GroupedNotes[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    loadLatestReport();
  }, [projectId]);

  const loadLatestReport = async () => {
    try {
      setLoading(true);
      setError("");

      const latestDate = await ReportAPI.getLatestDrsDate(projectId);
      const response = await ReportAPI.getDrsNotes(projectId, latestDate);

      setData(response);

    } catch (err) {
      console.error(err);
      setError("No DRS report available.");
    } finally {
      setLoading(false);
    }
  };

  const groupByNote = (notes: DRSNote[]) => {
    const map: { [key: number]: DRSNote & { images: string[] } } = {};

    notes.forEach(n => {
      if (!map[n.drsNotesNo]) map[n.drsNotesNo] = { ...n, images: [] };
      if (n.imageBase64) map[n.drsNotesNo].images.push(n.imageBase64);
    });

    return Object.values(map);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden">

      {/* ===== DASHBOARD STYLE HEADER ===== */}
      <div className="bg-[#1788ac] text-white font-semibold px-5 py-3 text-sm">
        STATEMENT NOTES REPORT
      </div>

      {/* ===== BODY ===== */}
      <div className="p-4">

        {loading && (
          <div className="text-gray-500">
            Loading latest DRS report...
          </div>
        )}

        {!loading && error && (
          <div className="text-red-500 font-medium">
            {error}
          </div>
        )}

        {!loading && !error && data.length === 0 && (
          <div className="text-gray-500">
            No DRS Notes available.
          </div>
        )}

        {!loading && !error && data.length > 0 && (
          <div className="space-y-6">

            {data.map((group, index) => (
              <div key={index}>

                {/* Section Header */}
                <div className="bg-gray-200 text-gray-800 font-medium px-3 py-2 rounded-md text-sm">
                  {group.noteType}
                </div>

                {/* Notes */}
                <div className="mt-3 space-y-3">

                  {groupByNote(group.notes).map((note) => (
                    <div
                      key={note.drsNotesNo}
                      className="bg-gray-50 border rounded-lg p-3"
                    >

                      <div className="text-sm font-medium text-gray-700">
                        {note.by || "General"}
                      </div>

                      <div className="text-gray-800 mt-1 text-sm">
                        {note.note}
                      </div>

                      {note.images.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {note.images.map((img, i) => (
                            <img
                              key={i}
                              src={img}
                              alt=""
                              className="w-32 h-24 object-cover rounded-md border"
                            />
                          ))}
                        </div>
                      )}

                    </div>
                  ))}

                </div>
              </div>
            ))}

          </div>
        )}

      </div>
    </div>
  );
}